// Iconify SVG normalization + sanitization. Originally lived at
// src/components/editor/controls/iconify.js (`normalizeIconSvg`) as a
// picker-only helper; moved here (Task 3, spec 2026-08-15 divider/icon
// elements, "Sicherheit" paragraph) so the canvas-side pick handler
// (useMediaAndContainerHandlers.ts#handleV2IconPick) can run the SAME
// normalization on an incoming `svg` string instead of trusting the
// postMessage payload verbatim — there is exactly ONE implementation, the
// old path is now a pure re-export shim (`export { normalizeIconSvg } from
// '@/editor-v2/utils/iconSvg';`), same precedent as pointerPercent.js/
// focalPointMath.js.
//
// Two concerns, always both, in this order:
//  1. Safety FIRST: `<script>` elements, any `on*` event-handler attribute,
//     and any `href`/`xlink:href` (any namespace-aliased `*:href`) that
//     isn't a local "#fragment" reference are stripped unconditionally.
//     None of the picker's three Iconify sets (lucide/simple-icons/cib)
//     legitimately need any of these — plain path/shape icons only — so
//     this never clips a real icon. This matters because `svg` travels
//     across a postMessage boundary (keditor-v2-icon-pick -> V2_ICON_PICK)
//     before landing here via innerHTML; a forged/compromised sender on
//     that channel must not be able to smuggle markup into the canvas.
//  2. Styling: size to 1em (inherits font-size), recolor concrete
//     strokes/fills to currentColor (inherits text color); "none" is left
//     untouched.
const EVENT_HANDLER_ATTR_RE = /^on/i;
const HREF_ATTR_RE = /(^|:)href$/i;

export function normalizeIconSvg(svgText) {
  if (!svgText || typeof svgText !== 'string') return '';
  let doc;
  try {
    doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
  } catch {
    return '';
  }
  const svg = doc.querySelector('svg');
  if (!svg) return '';

  // 1. Safety: drop <script> nodes before collecting the node list below, so
  // nothing removed here can re-enter via querySelectorAll('*').
  Array.from(svg.querySelectorAll('script')).forEach((node) => node.remove());
  const nodes = [svg].concat(Array.from(svg.querySelectorAll('*')));
  nodes.forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      if (EVENT_HANDLER_ATTR_RE.test(attr.name) || (HREF_ATTR_RE.test(attr.name) && attr.value.charAt(0) !== '#')) {
        el.removeAttribute(attr.name);
      }
    });
  });

  // 2. Styling (unchanged behavior from the pre-Task-3 picker-only version).
  svg.setAttribute('width', '1em');
  svg.setAttribute('height', '1em');
  nodes.forEach((el) => {
    ['stroke', 'fill'].forEach((attr) => {
      const v = el.getAttribute(attr);
      if (v && v !== 'none' && v.toLowerCase() !== 'currentcolor') {
        el.setAttribute(attr, 'currentColor');
      }
    });
  });
  return svg.outerHTML;
}

/** The icon as a CSS mask variable (`--wm-icon`): it takes the colour and size of its element. */
export function iconMaskeCss(svg) {
  return `--wm-icon:url("data:image/svg+xml,${encodeURIComponent(svg || '')}")`;
}

export const ICONIFY_API = 'https://api.iconify.design';

/**
 * Icon collections offered in the picker (moved from controls/iconify.js so the
 * canvas can resolve the AI tab's icon names with the same sets).
 * "simple-icons" and "cib" carry the brand/social logos (facebook, instagram,
 * linkedin, youtube, x, whatsapp, …) that "lucide" deliberately does not ship.
 *
 * Only attribution-free licences are listed here: lucide is ISC, simple-icons
 * and CoreUI Brands are CC0-1.0. Font Awesome is deliberately absent — its sets
 * are CC BY 4.0 and would oblige us to credit them on every published page.
 */
export const ICON_SETS = [
  { prefix: 'lucide', label: 'Standard' },
  { prefix: 'simple-icons', label: 'Social' },
  { prefix: 'cib', label: 'Brands' },
];

/**
 * JSON URL for one icon of a collection. The picker loads the picked icon
 * from here instead of the per-icon .svg endpoint: Iconify rate-limits the
 * .svg endpoint (HTTP 429 without CORS headers) once the grid has requested
 * its thumbnails, and the pick then failed without a visible reason.
 */
export function iconJsonUrl(ref) {
  const idx = ref.indexOf(':');
  return `${ICONIFY_API}/${ref.slice(0, idx)}.json?icons=${ref.slice(idx + 1)}`;
}

/**
 * Builds the same svg the .svg endpoint returns (1em sizing, collection or
 * icon viewBox) from an Iconify JSON response. Aliases resolve to their
 * parent. Returns null when the response does not contain the icon.
 */
export function svgFromIconJson(json, name) {
  const icons = (json && json.icons) || {};
  const alias = json && json.aliases && json.aliases[name];
  const icon = icons[name] || (alias && icons[alias.parent]);
  if (!icon || !icon.body) return null;
  const width = icon.width || json.width || 16;
  const height = icon.height || json.height || 16;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 ${width} ${height}">${icon.body}</svg>`;
}
